import { useEffect, useRef } from 'react';
import apiService from '../services/apiService';

/**
 * Custom hook to fetch and sync user profile data
 * Automatically fetches user profile on login and updates auth context
 * Only fetches once per session to prevent unnecessary API calls
 * 
 * @param {string} token - JWT token
 * @param {string} UID - User ID
 * @param {object} user - Current user object from auth context
 * @param {function} login - Login function from auth context
 */
export const useUserProfile = (token, UID, user, login) => {
  const fetchInProgressRef = useRef(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      // Skip if no auth, already fetched this session, or fetch in progress
      if (!token || !UID || fetchInProgressRef.current) return;
      
      // Check if we already have user data (from localStorage or previous fetch)
      if (user?.profileImageUrl !== undefined && user?.firstName && user?.lastName) {
        return; // Already have complete user data, skip fetch
      }
      
      fetchInProgressRef.current = true;
      
      try {
        const data = await apiService.get(`/v1/user/${UID}`, token);
        const userData = data.user || data;
        
        // Update auth context with latest user data including profile photo
        if (userData) {
          const updatedUser = {
            ...user,
            ...userData,
            // Ensure we preserve important fields
            id: userData._id || userData.id || UID,
            firstName: userData.firstName || userData.first_name,
            lastName: userData.lastName || userData.last_name,
            profileImageUrl: userData.profileImageUrl || "",
            role: userData.role,
            permissions: userData.permissions || []
          };
          
          // Re-login to update auth context (login() owns userData persistence)
          login(UID, token, null, updatedUser);
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
        // Don't logout on profile fetch error - user can still use the app
      } finally {
        fetchInProgressRef.current = false;
      }
    };

    if (token && UID) {
      fetchUserProfile();
    } else {
      fetchInProgressRef.current = false; // Reset when logged out
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, UID]); // Only depend on token and UID, not user or login
};
